/**
 * Shopping cart context provider
 * Supports multi-currency with automatic conversion
 */

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { CartItem, Product, Currency } from '../types';
import { useCurrency } from './CurrencyContext';

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  addToCart: (product: Product) => void;
  removeFromCart: (productId: string) => void;
  clearCart: () => void;
  /** Get cart total in the user's selected currency */
  getTotal: () => number;
  /** Get cart total in a specific currency (for checkout) */
  getTotalInCurrency: (currency: Currency) => number;
  /** Get formatted cart total string */
  getFormattedTotal: () => string;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps): JSX.Element {
  const [items, setItems] = useState<CartItem[]>([]);
  const { currency, convertPrice, formatPrice } = useCurrency();

  /**
   * Load cart from localStorage on mount
   */
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Failed to load cart from localStorage:', error);
      }
    }
  }, []);

  /**
   * Save cart to localStorage whenever it changes
   */
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  /**
   * Add product to cart
   */
  function addToCart(product: Product): void {
    setItems((prevItems) => {
      // Check if product already in cart
      const existingItem = prevItems.find((item) => item.productId === product.id);

      if (existingItem) {
        // Digital products: don't increase quantity, just return existing cart
        return prevItems;
      }

      // Add new item
      const newItem: CartItem = {
        productId: product.id,
        product,
        quantity: 1,
        price: product.price,
      };

      return [...prevItems, newItem];
    });
  }

  /**
   * Remove product from cart
   */
  function removeFromCart(productId: string): void {
    setItems((prevItems) => prevItems.filter((item) => item.productId !== productId));
  }

  /**
   * Clear entire cart
   */
  function clearCart(): void {
    setItems([]);
  }

  /**
   * Get cart total in the user's selected currency
   * Converts each item's price from its original currency
   */
  const getTotal = useCallback((): number => {
    return items.reduce((total, item) => {
      const itemCurrency = (item.price.currency as Currency) || Currency.GBP;
      const convertedAmount = convertPrice(item.price.amount, itemCurrency);
      return total + convertedAmount * item.quantity;
    }, 0);
  }, [items, convertPrice]);

  /**
   * Get cart total in a specific currency (useful for checkout)
   * For now, this just returns the total in the selected currency
   * The checkout will process in the user's selected currency
   */
  const getTotalInCurrency = useCallback((_targetCurrency: Currency): number => {
    // For checkout, we process in the user's selected currency
    // The payment processor handles the actual conversion if needed
    return getTotal();
  }, [getTotal]);

  /**
   * Get formatted cart total string in selected currency
   */
  const getFormattedTotal = useCallback((): string => {
    const total = getTotal();
    return formatPrice(total, currency);
  }, [getTotal, formatPrice, currency]);

  /**
   * Get total item count
   */
  const itemCount = items.length;

  const value: CartContextType = {
    items,
    itemCount,
    addToCart,
    removeFromCart,
    clearCart,
    getTotal,
    getTotalInCurrency,
    getFormattedTotal,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
